// /password-reset — request a reset link by email.
//
// Anti-enumeration: the backend always 202s, so the SPA always shows
// the same "if there's an account, the link is on its way" success
// state. We never differentiate by "email not found" — that would
// leak account membership.

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';

type LocationState = { email?: string } | null;

export function PasswordReset(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const state = useLocation().state as LocationState;
  const [email, setEmail] = useState(state?.email ?? '');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (status === 'sent') {
    return (
      <div className="card">
        <h2>{t('passwordReset.confirmation.title')}</h2>
        <p>
          <Trans
            i18nKey="passwordReset.confirmation.body"
            t={t}
            values={{ email }}
            components={[<strong key="email" />]}
          />
        </p>
        <p className="help">{t('passwordReset.confirmation.resendHint')}</p>
        <p>
          <Link to="/login">{t('passwordReset.confirmation.backToSignIn')}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>{t('passwordReset.request.title')}</h2>
      <p>{t('passwordReset.request.instructions')}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMessage(null);
          setStatus('submitting');
          void (async () => {
            try {
              await authApi.requestPasswordReset(email);
              setStatus('sent');
            } catch (err) {
              // Rate-limited (per-IP) is the only expected non-202.
              setErrorMessage(
                extractApiError(err)?.message ?? t('passwordReset.request.genericError')
              );
              setStatus('idle');
            }
          })();
        }}
      >
        <div className="form-row">
          <label htmlFor="email">{t('passwordReset.request.emailLabel')}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {errorMessage && <div className="banner banner--error">{errorMessage}</div>}
        <button className="primary" type="submit" disabled={status === 'submitting'}>
          {status === 'submitting'
            ? t('passwordReset.request.sending')
            : t('passwordReset.request.submit')}
        </button>
        <p style={{ marginTop: 16 }}>
          {t('passwordReset.request.rememberedPrefix')} <Link to="/login">{t('common:nav.signIn')}</Link>
        </p>
      </form>
    </div>
  );
}
